"use client";

import { SafeHydrate } from "@/SafeHydrate";
import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TriangleAlert as AlertTriangle, Building2, Calendar, Package, Store, Utensils } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
const applications = [{
  id: "food-distribution",
  title: "Food distribution",
  description: "Potential temporary refrigerated storage for shipments during staging and logistics handoffs. Confirm requirements with our team.",
  icon: "Package"
}, {
  id: "grocery-operations",
  title: "Grocery operations",
  description: "Potential temporary refrigerated storage for seasonal inventory, store remodels, or cooler overflow. Confirm requirements with our team.",
  icon: "Store"
}, {
  id: "catering",
  title: "Catering",
  description: "Potential temporary refrigerated storage for catering ingredients and prepared food. Confirm temperature and loading requirements with our team.",
  icon: "Utensils"
}, {
  id: "events",
  title: "Events",
  description: "Potential temporary refrigerated storage for food and beverages at events. Confirm site access, temperature, and loading requirements with our team.",
  icon: "Calendar"
}, {
  id: "emergency-cold-storage",
  title: "Emergency cold storage",
  description: "Potential temporary refrigerated storage during equipment downtime or facility disruptions, subject to location, timing, and availability.",
  icon: "AlertTriangle"
}, {
  id: "commercial-projects",
  title: "Commercial projects",
  description: "Potential temporary refrigerated storage for planned commercial projects, facility maintenance, or operational needs. Confirm suitability with our team.",
  icon: "Building2"
}];
export default function Applications() {
  const shouldReduceMotion = useReducedMotion();
  const renderIcon = (iconName: string) => {
    const iconClasses = "size-8 text-primary shrink-0";
    switch (iconName) {
      case "Package":
        return <Package className={iconClasses} aria-hidden="true" />;
      case "Store":
        return <Store className={iconClasses} aria-hidden="true" />;
      case "Utensils":
        return <Utensils className={iconClasses} aria-hidden="true" />;
      case "Calendar":
        return <SafeHydrate><Calendar className={iconClasses} aria-hidden="true" /></SafeHydrate>;
      case "AlertTriangle":
        return <AlertTriangle className={iconClasses} aria-hidden="true" />;
      case "Building2":
        return <Building2 className={iconClasses} aria-hidden="true" />;
      default:
        return <Package className={iconClasses} aria-hidden="true" />;
    }
  };
  return <section id="commonapplications" data-nav="light" className="bg-background py-14 md:py-20 lg:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
          <Badge variant="outline" className="mb-3 border-border bg-muted/50 text-xs font-semibold uppercase tracking-wider text-muted-foreground px-3 py-1 rounded-full">
            Potential Applications
          </Badge>
          <h2 className="font-default text-2xl sm:text-3xl md:text-4xl font-bold text-foreground tracking-tight">
            Potential Applications
          </h2>
          <p className="mt-3 text-base text-muted-foreground font-default">
            Examples of situations where a 53-foot reefer trailer may be useful — confirm suitability for your specific need with our team.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {applications.map((item, i) => <motion.div key={item.id} data-index={i} initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.45,
          delay: shouldReduceMotion ? 0 : i * 0.08,
          ease: "easeOut"
        }} className="h-full">
              <Card className="h-full rounded-lg border border-border bg-card p-6 shadow-sm flex flex-col justify-start transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md">
                <CardHeader className="p-0 gap-4 mb-4">
                  <div className="p-2.5 rounded-md bg-accent/60 w-fit">
                    {renderIcon(item.icon)}
                  </div>
                  <CardTitle className="font-default font-semibold text-foreground text-lg tracking-normal">
                    {item.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0 mt-auto">
                  <CardDescription className="text-sm text-muted-foreground font-default leading-relaxed">
                    {item.description}
                  </CardDescription>
                </CardContent>
              </Card>
            </motion.div>)}
        </div>
      </div>
    </section>;
}